import React, { useEffect, useMemo, useState } from 'react';
import './PayoutModal.css';

/**
 * Reusable Request Payout modal — used by both the marketer portal
 * (token auth) and the merchant referral dashboard (cookie auth). The
 * modal is purely presentational; callers inject the API surface via
 * `loadBanks`, `verifyAccount`, and `submit` props.
 *
 * Mobile-first: full-screen sheet under 600px, centered card above.
 */
function PayoutModal({
  open,
  onClose,
  walletBalance = 0,
  minimumPayout = 2000,
  defaultBank = null,
  loadBanks,
  verifyAccount,
  submit,
  onSuccess,
  showToast = () => {}
}) {
  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);

  const [amount, setAmount] = useState('');
  const [bankCode, setBankCode] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accountNameVerified, setAccountNameVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState(null);
  const [verificationProvider, setVerificationProvider] = useState(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Hydrate with prefill / wallet defaults whenever the modal opens
  useEffect(() => {
    if (!open) return;
    setAmount(String(walletBalance || ''));
    setBankCode(defaultBank?.bankCode || '');
    setBankName(defaultBank?.bankName || '');
    setAccountNumber(defaultBank?.accountNumber || '');
    setAccountName(defaultBank?.accountName || '');
    setAccountNameVerified(!!defaultBank?.accountVerified);
    setVerificationProvider(defaultBank?.accountVerified ? 'MANUAL' : null);
    setVerifyError(null);
    setSubmitError(null);
    setNote('');
  }, [open, walletBalance, defaultBank]);

  // Lazy-load banks the first time the modal opens
  useEffect(() => {
    if (!open || banks.length > 0 || banksLoading) return;
    let cancelled = false;
    setBanksLoading(true);
    loadBanks()
      .then(list => { if (!cancelled) setBanks(Array.isArray(list) ? list : []); })
      .catch(err => {
        if (!cancelled) showToast(err?.message || 'Could not load banks', 'error');
      })
      .finally(() => { if (!cancelled) setBanksLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const fmt = (n) => new Intl.NumberFormat('en-NG', {
    style: 'currency', currency: 'NGN', minimumFractionDigits: 0
  }).format(Number(n) || 0);

  // Reset verification whenever the account number or bank changes
  const handleBankChange = (code) => {
    const found = banks.find(b => b.code === code);
    setBankCode(code);
    setBankName(found?.name || '');
    setAccountNameVerified(false);
    setAccountName('');
    setVerifyError(null);
  };

  const handleAccountNumberChange = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 10);
    setAccountNumber(digits);
    if (accountNameVerified) {
      setAccountNameVerified(false);
      setAccountName('');
    }
    setVerifyError(null);
  };

  const handleVerify = async () => {
    if (!bankCode || accountNumber.length < 10) return;
    setVerifying(true);
    setVerifyError(null);
    try {
      const res = await verifyAccount({ bankCode, accountNumber });
      if (res?.success && res.accountName) {
        setAccountName(res.accountName);
        setAccountNameVerified(true);
        setVerificationProvider(res.provider || 'PAYSTACK');
      } else {
        setAccountNameVerified(false);
        setVerificationProvider(null);
        setVerifyError(res?.error || "Couldn't auto-verify — please type the account name exactly as it appears on your bank statement.");
      }
    } catch (err) {
      setAccountNameVerified(false);
      setVerificationProvider(null);
      setVerifyError(err?.response?.data?.error || err?.message || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const handleAccountNameChange = (val) => {
    setAccountName(val);
    if (accountNameVerified) {
      // If user edits the verified name, clear the verified state
      setAccountNameVerified(false);
      setVerificationProvider('MANUAL');
    } else {
      setVerificationProvider('MANUAL');
    }
  };

  const amtNum = Number(amount) || 0;

  const amountError = useMemo(() => {
    if (!amount) return null;
    if (amtNum < minimumPayout) return `Minimum is ${fmt(minimumPayout)}`;
    if (amtNum > walletBalance) return `You only have ${fmt(walletBalance)} available`;
    return null;
  }, [amount, amtNum, minimumPayout, walletBalance]);

  const canSubmit =
    !amountError &&
    amtNum >= minimumPayout &&
    bankCode &&
    bankName &&
    accountNumber.length >= 10 &&
    accountName.trim() &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await submit({
        amount: amtNum,
        bankName,
        bankCode,
        accountNumber,
        accountName: accountName.trim(),
        accountNameVerified,
        verificationProvider: verificationProvider || (accountNameVerified ? 'PAYSTACK' : 'MANUAL'),
        requestNote: note.trim() || null
      });
      if (result?.success || result?.payout) {
        showToast('Payout request submitted', 'success');
        onSuccess?.(result.payout);
        onClose();
      } else {
        setSubmitError(result?.error || 'Could not submit request');
      }
    } catch (err) {
      setSubmitError(err?.response?.data?.error || err?.message || 'Could not submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="payout-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="payout-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="payout-modal-title"
      >
        <div className="payout-modal__handle" aria-hidden="true" />
        <header className="payout-modal__header">
          <h2 id="payout-modal-title" className="payout-modal__title">Request Payout</h2>
          <button className="payout-modal__close" onClick={onClose} aria-label="Close">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </header>

        <div className="payout-modal__body">
          {/* Balance */}
          <div className="payout-modal__balance">
            <span className="payout-modal__balance-label">Available to withdraw</span>
            <span className="payout-modal__balance-value">{fmt(walletBalance)}</span>
          </div>

          {/* Amount */}
          <label className="payout-modal__field">
            <span className="payout-modal__label">Amount</span>
            <div className="payout-modal__amount-wrap">
              <span className="payout-modal__amount-prefix">₦</span>
              <input
                type="text"
                inputMode="decimal"
                className={`payout-modal__amount ${amountError ? 'payout-modal__amount--error' : ''}`}
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
                placeholder="0"
                autoFocus
              />
              <button
                type="button"
                className="payout-modal__max"
                onClick={() => setAmount(String(walletBalance))}
              >
                MAX
              </button>
            </div>
            <div className="payout-modal__hint">
              {amountError ? (
                <span className="payout-modal__hint--error">{amountError}</span>
              ) : (
                <span>Minimum {fmt(minimumPayout)} · partial payouts allowed</span>
              )}
            </div>
          </label>

          {/* Bank */}
          <label className="payout-modal__field">
            <span className="payout-modal__label">Bank</span>
            <select
              className="payout-modal__select"
              value={bankCode}
              onChange={(e) => handleBankChange(e.target.value)}
              disabled={banksLoading}
            >
              <option value="">{banksLoading ? 'Loading banks…' : 'Select your bank'}</option>
              {banks.map(b => (
                <option key={b.code} value={b.code}>{b.name}</option>
              ))}
            </select>
          </label>

          {/* Account number */}
          <label className="payout-modal__field">
            <span className="payout-modal__label">Account number</span>
            <div className="payout-modal__acct-wrap">
              <input
                type="text"
                inputMode="numeric"
                className="payout-modal__input"
                value={accountNumber}
                onChange={(e) => handleAccountNumberChange(e.target.value)}
                placeholder="10-digit account number"
                maxLength={10}
              />
              <button
                type="button"
                className="payout-modal__verify"
                onClick={handleVerify}
                disabled={!bankCode || accountNumber.length < 10 || verifying}
              >
                {verifying ? 'Verifying…' : 'Verify'}
              </button>
            </div>
          </label>

          {/* Account name */}
          <label className="payout-modal__field">
            <span className="payout-modal__label">
              Account name
              {accountNameVerified && (
                <span className="payout-modal__verified-badge">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Verified
                </span>
              )}
            </span>
            <input
              type="text"
              className={`payout-modal__input ${accountNameVerified ? 'payout-modal__input--locked' : ''}`}
              value={accountName}
              onChange={(e) => handleAccountNameChange(e.target.value)}
              placeholder="Full name as on your bank statement"
              readOnly={accountNameVerified}
            />
            {verifyError && (
              <div className="payout-modal__hint payout-modal__hint--warn">{verifyError}</div>
            )}
          </label>

          {/* Optional note */}
          <label className="payout-modal__field">
            <span className="payout-modal__label">Note <span className="payout-modal__optional">(optional)</span></span>
            <textarea
              className="payout-modal__textarea"
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 500))}
              placeholder="Anything we should know? e.g. preferred payout time"
              rows={2}
            />
          </label>

          {submitError && (
            <div className="payout-modal__error">{submitError}</div>
          )}
        </div>

        <footer className="payout-modal__footer">
          <button
            type="button"
            className="payout-modal__btn payout-modal__btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="payout-modal__btn payout-modal__btn--primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting ? 'Submitting…' : `Request ${amtNum > 0 ? fmt(amtNum) : 'Payout'}`}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default PayoutModal;
